import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import { api, editorApi } from "@/lib/api";
import { mapRascunhoFeicaoFromApi } from "@/lib/mappers";
import { useAreasPiloto, useEditorCamadas, useEditorRascunhos, useInspectionsWithQuadras, useQuadrasList } from "@/hooks";
import type { CamadaPerfil } from "@/types/editor-territorial";
import { useEditorMap } from "./useEditorMap";
import MapCanvas from "./components/MapCanvas";
import MapToolbar from "./components/MapToolbar";
import LayerPanel, { type SessionState } from "./components/LayerPanel";
import AttributePanel from "./components/AttributePanel";
import ReviewPanel from "./components/ReviewPanel";
import GeoJsonPreview from "./components/GeoJsonPreview";
import BottomStatusBar from "./components/BottomStatusBar";
import CreateTreeBar from "./components/CreateTreeBar";
import { CtaTreeCard, CtaTreesLayerControl } from "./components/CtaTreesLayer";

function generateUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function TerritorioConsoleWebgisPage() {
  const navigate = useNavigate();
  // Entrada pela inspecao territorial: ?area=&quadra=&modo=exemplar
  const [searchParams] = useSearchParams();
  const initialAreaParam = Number(searchParams.get("area")) || null;
  const initialQuadraParam = Number(searchParams.get("quadra")) || null;
  const [session, setSession] = useState<SessionState>({
    projetoId: generateUuid(),
    areaPilotoId: initialAreaParam,
    quadraId: initialAreaParam ? initialQuadraParam : null,
  });
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [geojsonPreview, setGeojsonPreview] = useState<unknown | null>(null);

  const { camadas, loading: camadasLoading } = useEditorCamadas();
  const { areasPiloto, loading: areasPilotoLoading } = useAreasPiloto();
  const { quadras, loading: quadrasLoading } = useQuadrasList(session.areaPilotoId);
  const { links: inspectionLinks } = useInspectionsWithQuadras();

  // Prioriza a area piloto real (SBV-REAL-001) como selecao inicial, assim que
  // a lista carregar, em vez de deixar o operador escolher manualmente entre
  // ela e a area demo antiga (SBV-DEMO-001, que nao deve ser usada como limite real).
  useEffect(() => {
    if (areasPilotoLoading || session.areaPilotoId !== null || areasPiloto.length === 0) return;
    const real = areasPiloto.find((a) => a.code === "SBV-REAL-001");
    if (real) setSession((prev) => ({ ...prev, areaPilotoId: real.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areasPiloto, areasPilotoLoading]);

  const selectedAreaPiloto = useMemo(
    () => areasPiloto.find((a) => a.id === session.areaPilotoId) ?? null,
    [areasPiloto, session.areaPilotoId]
  );
  const selectedQuadra = useMemo(() => quadras.find((q) => q.id === session.quadraId) ?? null, [quadras, session.quadraId]);
  const linkedInspectionsCount = useMemo(
    () => (selectedQuadra ? inspectionLinks.filter((l) => l.quadraId === selectedQuadra.id).length : 0),
    [inspectionLinks, selectedQuadra]
  );

  const {
    rascunhos,
    loading: rascunhosLoading,
    refetch,
  } = useEditorRascunhos({
    status: statusFilter ?? undefined,
    quadraRefId: selectedQuadra?.id ?? undefined,
  });

  const editor = useEditorMap();

  useEffect(() => {
    if (searchParams.get("modo") === "exemplar") editor.setInteractionMode("create-tree");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exemplares ja cadastrados no CTA, do projeto vinculado a area piloto
  // (ou de toda a organizacao, quando a area nao tem projeto).
  const ctaProjectId = selectedAreaPiloto?.projectId ?? undefined;
  const [ctaLoading, setCtaLoading] = useState(false);
  const [ctaError, setCtaError] = useState<string | null>(null);
  useEffect(() => {
    if (areasPilotoLoading) return;
    // A area real ainda vai ser selecionada automaticamente: evita buscar a organizacao inteira antes.
    if (session.areaPilotoId === null && areasPiloto.some((a) => a.code === "SBV-REAL-001")) return;
    let cancelled = false;
    setCtaLoading(true);
    setCtaError(null);
    api
      .treesGeoJson(ctaProjectId)
      .then((collection) => {
        if (cancelled) return;
        editor.setCtaTrees(collection);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        editor.setCtaTrees(null);
        setCtaError(err instanceof Error ? err.message : "Falha ao carregar os exemplares do CTA.");
      })
      .finally(() => {
        if (!cancelled) setCtaLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctaProjectId, areasPilotoLoading, session.areaPilotoId]);

  useEffect(() => {
    if (!rascunhosLoading) editor.loadFeaturesFromRascunhos(rascunhos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rascunhos, rascunhosLoading]);

  // --- zoom + boundary reference when área piloto / quadra changes ---------
  useEffect(() => {
    if (selectedQuadra?.geom) {
      editor.setReferenceBoundary(selectedQuadra.geom);
      editor.zoomToGeometry(selectedQuadra.geom);
    } else if (selectedAreaPiloto?.geom) {
      editor.setReferenceBoundary(selectedAreaPiloto.geom);
      editor.zoomToGeometry(selectedAreaPiloto.geom);
    } else {
      editor.setReferenceBoundary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQuadra, selectedAreaPiloto]);

  const showMessage = useCallback((text: string, isError = false) => {
    setMessage(isError ? `Erro: ${text}` : text);
    window.setTimeout(() => setMessage(null), 5000);
  }, []);

  const handleSelectPerfil = useCallback(
    (perfil: CamadaPerfil) => {
      editor.setPendingProfile({
        camadaDestino: perfil.camadaDestino,
        tipoObjeto: perfil.tipoObjeto,
        tipoGeometria: perfil.tipoGeometria,
      });
    },
    [editor]
  );

  const handleSaveDraft = useCallback(async () => {
    const attrs = editor.selectedAttrs;
    if (!attrs) return;

    if (!session.projetoId.trim()) {
      showMessage("Informe o UUID do projeto na sessão de edição antes de salvar.", true);
      return;
    }

    const geom = editor.getFeatureGeometryGeoJSON(attrs.featureId);
    if (!geom) {
      showMessage("Não foi possível ler a geometria da feição selecionada.", true);
      return;
    }

    setBusy(true);
    try {
      if (attrs.isNew || !attrs.rascunhoId) {
        const created = await editorApi.createRascunho({
          projeto_id: session.projetoId.trim(),
          quadra_id: selectedQuadra?.uuid ?? null,
          quadra_ref_id: selectedQuadra?.id ?? null,
          area_piloto_ref_id: session.areaPilotoId,
          projeto_ref_id: selectedAreaPiloto?.projectId ?? null,
          camada_destino: attrs.camadaDestino,
          tipo_objeto: attrs.tipoObjeto,
          geom,
          propriedades: attrs.propriedades,
        });
        editor.markFeatureSaved(attrs.featureId, mapRascunhoFeicaoFromApi(created));
        showMessage("Rascunho salvo com sucesso.");
      } else {
        const updated = await editorApi.updateRascunho(attrs.rascunhoId, { geom, propriedades: attrs.propriedades });
        editor.markFeatureSaved(attrs.featureId, mapRascunhoFeicaoFromApi(updated));
        showMessage("Rascunho atualizado com sucesso.");
      }
      refetch();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : "falha ao salvar rascunho", true);
    } finally {
      setBusy(false);
    }
  }, [editor, session, selectedQuadra, selectedAreaPiloto, showMessage, refetch]);

  const handleDelete = useCallback(async () => {
    const attrs = editor.selectedAttrs;
    if (!attrs) return;

    if (!attrs.rascunhoId) {
      if (!window.confirm("Excluir esta feição ainda não salva?")) return;
      editor.deleteSelectedFeature();
      return;
    }

    if (!window.confirm("Excluir este rascunho? Esta ação pode ser desfeita apenas pelo suporte, via banco de dados.")) return;

    setBusy(true);
    try {
      await editorApi.deleteRascunho(attrs.rascunhoId);
      editor.removeFeatureById(attrs.rascunhoId);
      showMessage("Rascunho excluído.");
      refetch();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : "falha ao excluir rascunho", true);
    } finally {
      setBusy(false);
    }
  }, [editor, showMessage, refetch]);

  const handleEnviarRevisao = useCallback(async () => {
    const attrs = editor.selectedAttrs;
    if (!attrs?.rascunhoId) return;
    setBusy(true);
    try {
      await editorApi.enviarRevisao(attrs.rascunhoId);
      editor.applyStatusToFeature(attrs.rascunhoId, "EM_REVISAO");
      showMessage("Enviado para revisão.");
      refetch();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : "falha ao enviar para revisao", true);
    } finally {
      setBusy(false);
    }
  }, [editor, showMessage, refetch]);

  const handleAprovar = useCallback(async () => {
    const attrs = editor.selectedAttrs;
    if (!attrs?.rascunhoId) return;
    setBusy(true);
    try {
      await editorApi.aprovar(attrs.rascunhoId);
      editor.applyStatusToFeature(attrs.rascunhoId, "APROVADO");
      showMessage("Rascunho aprovado.");
      refetch();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : "falha ao aprovar", true);
    } finally {
      setBusy(false);
    }
  }, [editor, showMessage, refetch]);

  const handleRejeitar = useCallback(
    async (motivo: string) => {
      const attrs = editor.selectedAttrs;
      if (!attrs?.rascunhoId) return;
      setBusy(true);
      try {
        await editorApi.rejeitar(attrs.rascunhoId, { motivo: motivo || null });
        editor.applyStatusToFeature(attrs.rascunhoId, "REJEITADO");
        showMessage("Rascunho rejeitado.");
        refetch();
      } catch (err) {
        showMessage(err instanceof Error ? err.message : "falha ao rejeitar", true);
      } finally {
        setBusy(false);
      }
    },
    [editor, showMessage, refetch]
  );

  const handleSolicitarAjuste = useCallback(
    async (motivo: string) => {
      const attrs = editor.selectedAttrs;
      if (!attrs?.rascunhoId) return;
      setBusy(true);
      try {
        await editorApi.solicitarAjuste(attrs.rascunhoId, { motivo: motivo || null });
        editor.applyStatusToFeature(attrs.rascunhoId, "AJUSTAR");
        showMessage("Ajuste solicitado.");
        refetch();
      } catch (err) {
        showMessage(err instanceof Error ? err.message : "falha ao solicitar ajuste", true);
      } finally {
        setBusy(false);
      }
    },
    [editor, showMessage, refetch]
  );

  const validateBeforePublish = useCallback((): string | null => {
    const attrs = editor.selectedAttrs;
    if (!attrs) return "Nenhuma feição selecionada.";
    if (!attrs.rascunhoId) return "Uma feição precisa ser salva como rascunho antes de publicar.";
    if (!selectedQuadra) {
      return "Selecione uma quadra operacional na sessão de edição antes de publicar (não é permitido publicar sem vínculo a uma quadra real).";
    }
    if (attrs.tipoObjeto === "ARVORE" && !attrs.propriedades.codigo) {
      return "Preencha o campo Código antes de publicar uma árvore.";
    }
    return null;
  }, [editor, selectedQuadra]);

  const handlePublicar = useCallback(async () => {
    const validationError = validateBeforePublish();
    if (validationError) {
      showMessage(validationError, true);
      return;
    }

    const attrs = editor.selectedAttrs;
    if (!attrs?.rascunhoId) return;
    setBusy(true);
    try {
      const result = await editorApi.publicar(attrs.rascunhoId);
      editor.applyStatusToFeature(attrs.rascunhoId, result.status);
      showMessage("Rascunho publicado com sucesso.");
      refetch();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : "falha ao publicar", true);
    } finally {
      setBusy(false);
    }
  }, [editor, validateBeforePublish, showMessage, refetch]);

  // Fluxo B (§5): hands the coordinate to the FIQ-1 form instead of
  // reimplementing registration here. The project comes from the selected pilot
  // area so the operator does not have to pick it again on the other screen.
  const handleConfirmTreePoint = useCallback(() => {
    const point = editor.treePoint;
    if (!point) return;
    const params = new URLSearchParams({
      lat: String(point[1]),
      lng: String(point[0]),
    });
    if (selectedAreaPiloto?.projectId) {
      params.set("project_id", String(selectedAreaPiloto.projectId));
    }
    editor.clearTreePoint();
    editor.setInteractionMode("select");
    navigate(`/cta/novo?${params.toString()}`);
  }, [editor, selectedAreaPiloto, navigate]);

  const handleCancelTreePoint = useCallback(() => {
    editor.clearTreePoint();
    editor.setInteractionMode("select");
  }, [editor]);

  const handleExportGeoJson = useCallback(() => {
    setGeojsonPreview(editor.exportGeoJSON());
  }, [editor]);

  // --- keyboard shortcuts ---------------------------------------------------
  useEffect(() => {
    function isEditableContext(target: EventTarget | null) {
      const isEditableTag = (el: Element | null) => {
        const tag = el?.tagName;
        return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (el as HTMLElement | null)?.isContentEditable === true;
      };
      // Checa tanto o alvo do evento quanto o elemento com foco no documento:
      // alguns fluxos de digitação disparam keydown com um alvo que nao e
      // o input focado, entao confiar so em event.target pode deixar passar
      // Delete/Backspace enquanto o usuario ainda esta editando um campo.
      return isEditableTag(target as Element | null) || isEditableTag(document.activeElement);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (isEditableContext(event.target)) return;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z" && !event.shiftKey) {
        event.preventDefault();
        editor.undo();
      } else if ((event.ctrlKey || event.metaKey) && (event.key.toLowerCase() === "y" || (event.shiftKey && event.key.toLowerCase() === "z"))) {
        event.preventDefault();
        editor.redo();
      } else if (event.key === "Delete" || event.key === "Backspace") {
        if (editor.selectedAttrs) {
          event.preventDefault();
          void handleDelete();
        }
      } else if (event.key === "Escape") {
        editor.setInteractionMode("select");
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editor, handleDelete]);

  const activeCamadaDestino = editor.pendingProfile?.camadaDestino ?? null;

  const sessionHandlers = useMemo(
    () => ({
      onSessionChange: (patch: Partial<SessionState>) => setSession((prev) => ({ ...prev, ...patch })),
      onGenerateProjetoId: () => setSession((prev) => ({ ...prev, projetoId: generateUuid() })),
    }),
    []
  );

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)]">
      <PageHeader
        title="Console de Edição Territorial"
        subtitle="Editor WebGIS para desenhar, editar e revisar geometrias territoriais preliminares antes da publicação oficial."
      />

      <div className="flex-1 min-h-0 flex flex-col border border-background-200/70 rounded-card overflow-hidden">
        <MapToolbar
          interactionMode={editor.interactionMode}
          drawMode={editor.drawMode}
          onSetInteractionMode={editor.setInteractionMode}
          onSetDrawMode={editor.setDrawMode}
          canUndo={editor.canUndo}
          canRedo={editor.canRedo}
          onUndo={editor.undo}
          onRedo={editor.redo}
          hasSelection={Boolean(editor.selectedAttrs)}
          onDelete={handleDelete}
          onExportGeoJson={handleExportGeoJson}
          onSaveDraft={handleSaveDraft}
          onSendReview={handleEnviarRevisao}
          saving={busy}
        />

        <div className="flex-1 min-h-0 flex">
          <LayerPanel
            session={session}
            onSessionChange={sessionHandlers.onSessionChange}
            onGenerateProjetoId={sessionHandlers.onGenerateProjetoId}
            areasPiloto={areasPiloto}
            areasPilotoLoading={areasPilotoLoading}
            quadras={quadras}
            quadrasLoading={quadrasLoading}
            selectedQuadra={selectedQuadra}
            linkedInspectionsCount={linkedInspectionsCount}
            camadas={camadas}
            camadasLoading={camadasLoading}
            activeCamadaDestino={activeCamadaDestino}
            onSelectPerfil={handleSelectPerfil}
            hiddenCamadas={editor.hiddenCamadas}
            onToggleVisibility={editor.toggleCamadaVisibility}
            lockedCamadas={editor.lockedCamadas}
            onToggleLock={editor.toggleCamadaLock}
            layerOpacity={editor.layerOpacity}
            onOpacityChange={editor.setLayerOpacity}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            referenceSection={
              <CtaTreesLayerControl
                count={editor.ctaTreeCount}
                loading={ctaLoading}
                error={ctaError}
                visible={editor.ctaTreesVisible}
                onToggleVisible={editor.setCtaTreesVisible}
                onZoom={editor.zoomToCtaTrees}
                scopeLabel={ctaProjectId ? `Projeto da área piloto (#${ctaProjectId})` : "Todos os projetos da organização"}
              />
            }
          />

          <div className="flex-1 min-h-0 relative flex">
            <MapCanvas
              mapDivRef={editor.mapDivRef}
              baseLayer={editor.baseLayer}
              onBaseLayerChange={editor.setBaseLayer}
              measurement={editor.measurement}
            />
            {editor.interactionMode === "create-tree" && (
              <CreateTreeBar
                point={editor.treePoint}
                quadraLabel={selectedQuadra?.code ?? null}
                onConfirm={handleConfirmTreePoint}
                onCancel={handleCancelTreePoint}
              />
            )}
          </div>

          <div className="w-72 shrink-0 border-l border-background-200/70 bg-white overflow-y-auto flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto">
              {!editor.selectedAttrs && editor.selectedCtaTree ? (
                <CtaTreeCard tree={editor.selectedCtaTree} onClose={editor.clearSelectedCtaTree} />
              ) : (
                <AttributePanel attrs={editor.selectedAttrs} onSaveProps={(props) => editor.updateSelectedFeatureProps(props)} />
              )}
            </div>
            {editor.selectedAttrs && (
              <ReviewPanel
                attrs={editor.selectedAttrs}
                busy={busy}
                onEnviarRevisao={handleEnviarRevisao}
                onAprovar={handleAprovar}
                onRejeitar={handleRejeitar}
                onSolicitarAjuste={handleSolicitarAjuste}
                onPublicar={handlePublicar}
              />
            )}
          </div>
        </div>

        <BottomStatusBar cursorCoordinate={editor.cursorCoordinate} featureCount={editor.featureCount} message={message} />
      </div>

      <GeoJsonPreview geojson={geojsonPreview} onClose={() => setGeojsonPreview(null)} />
    </div>
  );
}
